import React, { memo, useCallback, useMemo } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import _get from 'lodash/get';
import _isEqual from 'lodash/isEqual';

import { useDevice } from '@bonnet/next/device';
import { usePageEvent } from '@bonnet/next/use-page-event';

import { useFeatures } from '@atc/features';

import { sendClick, sendImpressions } from 'reaxl-analytics';

import { extractName } from 'axl-ducks';

// Utilities
import { getDealerDetailUrl } from '@/utilities/ddpUrlBuilder';
import { getAlphaCriteriaMatches } from '@/utilities/getAlphaCriteriaMatches';
import { getAlphaClickType, getAlphaListingType } from '@/utilities/getAlphaTaggingType';
import { saveAlphaListing } from '@/utilities/saveListing';
import vdpUrlBuilder from '@/utilities/vdpUrlBuilder';

import { hideAlphaCTAClick } from '@/analytics/srp/srpInventoryDealerEvents';
import { alphaImpression } from '@/analytics/srpAnalyticsHandlers';

import compareListingsDuck from '@/ducks/compareListingsDuck.js';
import paymentsDuck from '@/ducks/paymentsDuck.js';
import savedInventoryDuck from '@/ducks/savedInventoryDuck.js';
import srpActiveEmailListingDuck from '@/ducks/srp/srpActiveEmailListingDuck.js';
import srpActiveInteractionDuck from '@/ducks/srp/srpActiveInteractionDuck.js';
import srpFiltersDuck from '@/ducks/srp/srpFiltersDuck.js';
import srpSponsorshipDuck from '@/ducks/srp/srpSponsorshipDuck.js';
import userPreferencesDuck from '@/ducks/userPreferencesDuck.js';

// Fetcher

import PaymentCalculationFetcher from '@/fetchers/PaymentCalculationFetcher';

import useVdpNavigation from '@/hooks/useVdpNavigation';

import AlphaModule from '@/modules/AlphaModule';

import AlphaShowcase from '@/components/AlphaShowcase';
import AlphaShowcaseInfoText from '@/components/AlphaShowcaseInfoText/index.js';

import AlphaHandlers from './analytics/AlphaHandlers';
import InventoryResultsHandlers from './analytics/InventoryResultsHandlers';

function InventoryListingPlaceholder({ orientation = 'portrait', ...rest }) {
    if (orientation === 'landscape') {
        return (
            <div
                data-cmp="inventoryListingPlaceholder"
                className="margin-bottom-5"
            >
                <div
                    className="inventory-listing cursor-pointer display-flex flex-column panel panel-default"
                >
                    <div
                        className="item-card display-flex skeleton__listing skeleton__loading"
                        style={{ height: 227 }}
                    >
                        <div className="padding-0 margin-bottom-3 margin-bottom-sm-0">
                            <div
                                className="skeleton__image skeleton__placeholder"
                                style={{ width: 303 }}
                            />
                        </div>
                        <div className="col-sm-8 col-md-8 col-lg-9 padding-horizontal-4 padding-top-3">
                            <div className="row">
                                <div className="col-xs-7 col-sm-5">
                                    <div className="skeleton__headline skeleton__placeholder" />
                                </div>
                                <div className="col-xs-3 col-sm-2 col-xs-offset-2 col-sm-offset-5">
                                    <div className="skeleton__text skeleton__placeholder" />
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-xs-7 col-sm-5">
                                    <div className="skeleton__text skeleton__placeholder" />
                                </div>
                                <div className="col-xs-3 col-sm-2 col-xs-offset-2 col-sm-offset-5">
                                    <div className="skeleton__text skeleton__placeholder" />
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-xs-5 col-sm-3">
                                    <div className="skeleton__headline skeleton__placeholder" />
                                </div>
                                <div className="col-xs-2 col-sm-1 col-xs-offset-5 col-sm-offset-8">
                                    <div className="skeleton__text skeleton__placeholder" />
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-xs-12">
                                    <div className="skeleton__spacer" />
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-xs-10 col-md-5">
                                    <div className="skeleton__text skeleton__placeholder" />
                                </div>
                                <div className="col-xs-8 col-md-3 col-lg-4">
                                    <div className="skeleton__text skeleton__placeholder" />
                                </div>
                                <div className="col-xs-2 col-lg-1">
                                    <div className="skeleton__text skeleton__placeholder" />
                                </div>
                                <div className="col-xs-2 col-lg-1">
                                    <div className="skeleton__text skeleton__placeholder" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
    return (
        <div
            data-cmp="inventoryListingPlaceholder"
        >
            <div
                className="inventory-listing cursor-pointer display-flex flex-column panel panel-default"
            >
                <div
                    className="row item-card skeleton__listing skeleton__loading skeleton__fixedheight"
                    style={{ height: 590 }}
                >
                    <div className="col-xs-12 padding-0">
                        <div
                            className="skeleton__image_portrait skeleton__placeholder"
                            data-cmp="inventoryListingPlaceholderImage"
                            style={{ height: 306 }}
                        />
                    </div>
                    <div className="col-xs-12 padding-4">
                        <div className="row">
                            <div className="col-xs-12">
                                <div className="skeleton__headline skeleton__placeholder" />
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-xs-6">
                                <div className="skeleton__subtext skeleton__placeholder" />
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-xs-11 margin-bottom-1 margin-bottom-sm-2 margin-bottom-lg-1">
                                <div className="skeleton__subtext skeleton__placeholder" />
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-xs-4 margin-bottom-2">
                                <div className="skeleton__subtext skeleton__placeholder" />
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-xs-12">
                                <div className="skeleton__spacer" />
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-xs-8">
                                <div className="skeleton__headline skeleton__placeholder" />
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-xs-7 margin-bottom-3 margin-bottom-sm-6 margin-bottom-lg-3">
                                <div className="skeleton__text skeleton__placeholder" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
/*
 * The AlphaShowcaseContainer will display either the InventoryListingAlpha or OwnerAlphaListing from reaxl-organisms
 * based on the data it receives.
 */
function AlphaShowcaseContainerComponent() {
    const device = useDevice();
    const isXs = _get(device, 'is.xs', false);
    const {
        alpha_showcase: [, { owner_inventory_handler: enableOwnerInventoryHandler }],
        alpha_exact_match: [inventoryExactMatch],
        ddp_link: [isDdpLinkEnabled],
        nds: ndsFeature,
        my_wallet: [enableMyWallet],
        personalization: [isPersonalizationEnabled, { listings_saved: authenticatedSavesOnly }],
        retail_engine: [enableRetailEngine],
    } = useFeatures([
        'alpha_showcase',
        'alpha_exact_match',
        'ddp_link',
        'nds',
        'my_wallet',
        'personalization',
        'retail_engine',
    ]);

    const { onPageEnter } = usePageEvent('alphaShowcase');
    const navigateToVdp = useVdpNavigation();
    const dispatch = useDispatch();

    const compareToggle = useSelector(userPreferencesDuck.selectors.getCompareToggle);
    const showAlphaWalletPayment = useSelector((state) => paymentsDuck.selectors.getMyWalletInteraction(state, 'paymentEmphasis'));
    const compareListingIds = useSelector(compareListingsDuck.selectors.getActiveListingIds, _isEqual);
    const isSponsorshipSelected = useSelector(srpSponsorshipDuck.selectors.getDuckState);
    const savedListingIds = useSelector(savedInventoryDuck.selectors.getSavedInventoryIds, _isEqual);
    const filterChips = useSelector(srpFiltersDuck.selectors.getSelectedFilterChips, _isEqual);
    const filtersValues = useSelector(srpFiltersDuck.selectors.getFiltersValues, _isEqual);
    const isAlphaMarket = useSelector(AlphaModule.duck.selectors.isAlphaMarket);
    const isOwnerAlpha = !useSelector(AlphaModule.duck.selectors.isListingAlpha);
    const isLoadingAlpha = useSelector(AlphaModule.duck.selectors.isLoading);
    const similarListingsCount = useSelector(AlphaModule.duck.selectors.getSimilarListingsCount);
    const carouselListings = useSelector(AlphaModule.duck.selectors.getActiveInventory, (oldVal, newVal) => oldVal[0]?.id === newVal[0]?.id);
    const owner = useSelector(AlphaModule.duck.selectors.getOwner, (oldVal, newVal) => oldVal?.id === newVal?.id);
    const showcaseListing = useMemo(() => (carouselListings[0] ? { ...JSON.parse(JSON.stringify(carouselListings[0])), owner } : null), [carouselListings, owner]);

    /* default values */
    const dealerDetailUrl = useMemo(() => _get(getDealerDetailUrl(owner), 'href', ''), [owner]);
    const listingId = useMemo(() => _get(showcaseListing, 'id', ''), [showcaseListing]);
    const make = useMemo(() => extractName(_get(showcaseListing, 'make', '')), [showcaseListing]);
    const model = useMemo(() => extractName(_get(showcaseListing, 'model', '')), [showcaseListing]);
    const ownerId = useMemo(() => _get(owner, 'id', ''), [owner]);
    const vdpWebsite = useMemo(() => vdpUrlBuilder({
        baseUrl: _get(showcaseListing, 'vdpBaseUrl'),
        clickType: getAlphaClickType(isAlphaMarket, isOwnerAlpha),
    }), [showcaseListing, isAlphaMarket, isOwnerAlpha]);

    /* ui context props*/
    const uiContextData = useMemo(() => {
        const { isExactMatch } = getAlphaCriteriaMatches({ filterChips, filtersValues, showcaseListing });
        let uiContext;
        if (inventoryExactMatch) {
            uiContext = !isOwnerAlpha && isExactMatch ? 'inventory' : 'owner';
        } else {
            uiContext = !isOwnerAlpha ? 'inventory' : 'owner';
        }
        return { uiContext, isExactMatch };
    }, [filterChips, filtersValues, showcaseListing, inventoryExactMatch, isOwnerAlpha]);

    const { uiContext, isExactMatch } = uiContextData;
    const parentId = useMemo(() => getAlphaListingType(isAlphaMarket, isOwnerAlpha), [isAlphaMarket, isOwnerAlpha]);
    const orientation = useMemo(() => (isXs ? 'portrait' : 'landscape'), [isXs]);
    const ownerHasLocation = useMemo(() => Object.keys(_get(owner, 'location', {})).length > 0, [owner]);

    /* on initial page load & when the page instance id changes, fire an impression */
    onPageEnter(() => {
        if (ownerId) {
            sendImpressions({
                name: alphaImpression,
            });
        }
    });

    const updateMyWallet = useCallback((state) => dispatch(paymentsDuck.creators.updateMyWallet(state)), [dispatch]);
    const updateListingPayments = useCallback((state) => dispatch(paymentsDuck.creators.updateListingPayments(state)), [dispatch]);

    /*
     * import alpha analytics handlers
     */
    const handleAlphaWalletClick = AlphaHandlers.useAlphaWalletClick(updateMyWallet, showcaseListing, parentId);

    const handleCollapsibleHeaderClick = AlphaHandlers.useCollapsibleHeaderClick(showcaseListing, parentId);

    const handleAlphaListingClick = AlphaHandlers.useAlphaListingClick(showcaseListing, parentId, isOwnerAlpha, navigateToVdp, vdpWebsite, dispatch);

    const handleOwnerInventoryClick = AlphaHandlers.useOwnerInventoryClick(showcaseListing, ownerId, enableOwnerInventoryHandler, dealerDetailUrl, parentId);

    const handleViewPaymentDetailsClick = AlphaHandlers.useViewPaymentDetailsClick(showcaseListing, parentId);

    const handlePaymentCTAClick = AlphaHandlers.usePaymentCTAClick(showcaseListing, parentId, updateMyWallet);

    const handlePricingDetailsClick = AlphaHandlers.usePricingDetailsClick(showcaseListing, navigateToVdp, vdpWebsite);

    const handleTileClick = AlphaHandlers.useTileClick(showcaseListing, ownerId, parentId);

    const handleInventoryListingClick = AlphaHandlers.useInventoryListingClick(parentId, navigateToVdp, dispatch);

    const handleInViewCarouselChange = AlphaHandlers.useInViewCarouselChange(carouselListings);

    const handleReadyCallback = AlphaHandlers.useOnCarouselLoad(carouselListings);

    const hideAlphaShowcase = useSelector(srpActiveInteractionDuck.selectors.getHideAlpha);

    const handleSeeVehicleDetailsClick = InventoryResultsHandlers.useSeeVehicleDetailsClick(sendClick, filtersValues);

    const handleCheckDealStatusClick = InventoryResultsHandlers.useCheckDealStatusClick(sendClick, filtersValues);

    const handleEmailOwnerClick = useCallback(() => {
        dispatch(srpActiveEmailListingDuck.creators.setActiveResults([listingId]));
        dispatch(srpActiveInteractionDuck.creators.setKeys({
            ownerId: uiContext === 'owner' ? ownerId : '',
            parentId,
            showEmailModal: true,
            clickType: 'alpha',
        }));

    }, [dispatch, listingId, ownerId, parentId, uiContext]);

    const emailOwnerProps = useMemo(() => ({
        context: 'alpha',
        parentId,
        onCTAClick: handleEmailOwnerClick,
        labelKey: 'checkAvailability',
    }), [parentId, handleEmailOwnerClick]);

    const listingCTA = useMemo(() => ({ href: vdpWebsite }), [vdpWebsite]);
    const onSaveToggle = useCallback((event, listing, wasSaved) => {
        dispatch(
            saveAlphaListing({
                event,
                listing,
                wasSaved,
                sendClick,
                parentId,
            })
        );
    }, [dispatch, parentId]);

    const carouselSettings = useMemo(() => ({
        onReady: handleReadyCallback,
        fullWidthScroll: true,
        visibilityThreshold: 0.9,
    }), [handleReadyCallback]);

    const personalPaymentProps = useMemo(() => ({
        fetcher: PaymentCalculationFetcher,
        paymentsEnabled: enableRetailEngine,
        ndsFeature,
    }), [enableRetailEngine, ndsFeature]);

    const paymentsCTA = useMemo(
        () => ({ onClick: handleViewPaymentDetailsClick }),
        [handleViewPaymentDetailsClick]
    );

    const handleHideAlpha = useCallback(
        (event) => {
            dispatch(srpActiveInteractionDuck.creators.setKeys({
                hideAlpha: true,
            }));
            const extra = {
                inventoryId: listingId,
                parentId,
            };
            sendClick(hideAlphaCTAClick, event, extra);
        },
        [listingId, parentId, dispatch]
    );

    const hasAlpha = useMemo(() => !!owner && owner.name && listingId, [owner, listingId]);
    const showCompareButton = useMemo(() => compareToggle && compareListingIds?.length < 5, [compareToggle, compareListingIds?.length]);
    const shouldShowAlpha = useMemo(() => hasAlpha && !hideAlphaShowcase && make && model, [hasAlpha, hideAlphaShowcase, make, model]);
    const shouldShowPlaceholder = useMemo(() => isLoadingAlpha && hasAlpha && !hideAlphaShowcase, [isLoadingAlpha, hasAlpha, hideAlphaShowcase]);
    const shouldRenderNull = useMemo(() => !owner || isSponsorshipSelected, [owner, isSponsorshipSelected]);

    // Early returns for better performance
    if (shouldRenderNull) {
        return null;
    }

    // Only show the placeholder when filtering if there is an existing alpha to preserve space for.
    if (shouldShowPlaceholder) {
        return (
            <>
                <AlphaShowcaseInfoText
                    dealerName={owner.name}
                    uiContext={uiContext}
                />
                <InventoryListingPlaceholder orientation={orientation} />
            </>
        );
    }

    /* only render alpha if there is an owner object */
    return shouldShowAlpha ? (
        <AlphaShowcase
            enableMyWallet={enableMyWallet}
            emailOwnerProps={emailOwnerProps}
            listing={showcaseListing || {}}
            listings={carouselListings}
            listingsCount={similarListingsCount > 0 ? similarListingsCount : null}
            listingCTA={listingCTA}
            onHideAlpha={handleHideAlpha}
            onAlphaWalletClick={handleAlphaWalletClick}
            onCollapsibleHeaderClick={handleCollapsibleHeaderClick}
            onAlphaListingClick={handleAlphaListingClick}
            onInventoryListingClick={handleInventoryListingClick}
            onSettle={handleInViewCarouselChange}
            onPricingDetailClick={handlePricingDetailsClick}
            onSaveToggle={isPersonalizationEnabled || authenticatedSavesOnly ? onSaveToggle : null}
            showCompareButton={showCompareButton}
            onTileClick={handleTileClick}
            onViewOwnerInventoryClick={(isDdpLinkEnabled && ownerHasLocation) ? handleOwnerInventoryClick : null}
            owner={owner}
            orientation={orientation}
            parentId={parentId}
            paymentsCTA={paymentsCTA}
            personalPaymentProps={personalPaymentProps}
            savedListingIds={savedListingIds}
            showAlphaPaymentRibbon={!!enableMyWallet && !!showAlphaWalletPayment}
            uiContext={uiContext}
            updateListingPayments={updateListingPayments}
            carouselSettings={carouselSettings}
            onPaymentCTAClick={handlePaymentCTAClick}
            isExactMatch={isExactMatch}
            onActiveDealDetailsClick={handleSeeVehicleDetailsClick}
            onCheckStatusClick={handleCheckDealStatusClick}
        />
    ) : null;
}

export const AlphaShowcaseContainer = memo(AlphaShowcaseContainerComponent);

export default AlphaShowcaseContainer;
